import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./sessionCardStyles";
import { bookSession, cancelSession, getExistingZoomLink } from "./timeTableService";
import { extractLocalTime } from "../../../utils/dateTimeUtils";
import { sessionActions } from "./sessionActions";
import { FaRegCopy, FaCheck, FaShareAlt, FaArrowRight, FaBookOpen } from "react-icons/fa";

const isMobileDevice = () => window.innerWidth <= 768;

const getPlanTypeLabel = (planType) => {
  switch (planType) {
    case "GROUP":           return "Group";
    case "GROUP_DISCUSSION": return "Group Discussion";
    case "ONE_ON_ONE":      return "1-on-1";
    default:                return planType;
  }
};

const SessionCard = ({ session, refreshSessionStatus, highlighted }) => {
  const navigate = useNavigate();
  const [loadingAction,    setLoadingAction]    = useState(false);
  const [hovered,          setHovered]          = useState(false);
  const [openAgenda,       setOpenAgenda]       = useState(false);
  const [minutesToStart,   setMinutesToStart]   = useState(null);
  const [minutesToEnd,     setMinutesToEnd]     = useState(null);
  const [showAlert,        setShowAlert]        = useState(false);
  const [alertMessage,     setAlertMessage]     = useState("");
  const [copiedSectionId,  setCopiedSectionId]  = useState(null);
  const [linkCopied,       setLinkCopied]       = useState(false);
  const [isMobile,         setIsMobile]         = useState(isMobileDevice());
  const [topicHovered,     setTopicHovered]     = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── Time calculation ── */
  useEffect(() => {
    if (!session?.startTime || !session?.endTime) return;
    const calculate = () => {
      const now = Date.now();
      setMinutesToStart(Math.floor((new Date(session.startTime).getTime() - now) / 60000));
      setMinutesToEnd(Math.floor((new Date(session.endTime).getTime() - now) / 60000));
    };
    calculate();
    const interval = setInterval(calculate, 60000);
    return () => clearInterval(interval);
  }, [session?.startTime, session?.endTime]);

  /* ── Session state ── */
  const isCancelled    = session?.cancelled === true;
  const isBooked       = session.buttonAction === sessionActions.CANCEL || session.buttonAction === sessionActions.JOIN;
  const isExpired      = minutesToEnd !== null && minutesToEnd < 0;
  const isPreparing    = isBooked && minutesToStart !== null && minutesToStart <= 10 && minutesToStart > 6;
  const isJoinWindow   = isBooked && ((minutesToStart <= 6 && minutesToEnd >= 0) || (minutesToStart < 0 && minutesToEnd >= 0));
  const isBookingClosed = !isBooked && minutesToStart !== null && minutesToStart <= 10 && minutesToEnd >= 0;
  const isSessionFull  = !isBooked && session.planType !== "ONE_ON_ONE" &&
    (session.buttonAction === sessionActions.FULL || (session.remainingSlots !== undefined && session.remainingSlots <= 0));

  /* ── Button label ── */
  const getButtonLabel = () => {
    if (isCancelled)     return "CANCELLED";
    if (isExpired)       return "EXPIRED";
    if (isJoinWindow)    return "JOIN NOW";
    if (isPreparing)     return `Preparing… ${minutesToStart}m`;
    if (isSessionFull)   return "SESSION FULL";
    if (isBookingClosed) return "BOOKING CLOSED";
    switch (session.buttonAction) {
      case sessionActions.BOOK:    return "BOOK SESSION";
      case sessionActions.CANCEL:  return "CANCEL";
      case sessionActions.UPGRADE: return "UPGRADE PLAN";
      default:                     return session.buttonAction;
    }
  };

  /* ── Button style ── */
  const getButtonStyle = () => {
    if (isCancelled || isExpired) return styles.expiredBtn;
    if (isJoinWindow)    return styles.joinBtn;
    if (isPreparing)     return styles.preparingBtn;
    if (isSessionFull || isBookingClosed) return styles.bookingClosedBtn;
    switch (session.buttonAction) {
      case sessionActions.BOOK:    return styles.bookBtn;
      case sessionActions.CANCEL:  return styles.cancelBtn;
      case sessionActions.UPGRADE: return styles.upgradeBtn;
      default:                     return styles.bookBtn;
    }
  };

  /* ── Action handler ── */
  const handleButtonClick = async () => {
    if (loadingAction || isCancelled || isExpired || isPreparing || isBookingClosed || isSessionFull) return;
    const token = localStorage.getItem("token");
    if (!token && session.buttonAction === sessionActions.BOOK) {
      window.location.href = "/register";
      return;
    }
    setLoadingAction(true);
    try {
      if (isJoinWindow) {
        const res = await getExistingZoomLink(session.id);
        if (res.data && res.data !== "Zoom link will be available soon") {
          window.open(res.data, "_blank");
        } else {
          setAlertMessage("Zoom link will be available soon");
          setShowAlert(true);
        }
      } else {
        switch (session.buttonAction) {
          case sessionActions.BOOK:    await bookSession(session.id); break;
          case sessionActions.CANCEL:  await cancelSession(session.id); break;
          case sessionActions.UPGRADE: window.location.href = "/plans"; break;
          default: break;
        }
      }
      refreshSessionStatus?.();
    } finally {
      setLoadingAction(false);
    }
  };

  /* ── Share ── */
  const handleShare = async () => {
    const url = `${window.location.origin}/timetable?session=${session.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  /* ── Copy section ── */
  const handleCopySection = async (section) => {
    try {
      const text = section.questions?.map((q, i) => `${i + 1}. ${q.questionText}`).join("\n");
      await navigator.clipboard.writeText(text);
      setCopiedSectionId(section.id);
      setTimeout(() => setCopiedSectionId(null), 1500);
    } catch {}
  };

  const isOneOnOne = session.planType === "ONE_ON_ONE";

  /* ── Render ── */
  return (
    <div
      style={{
        ...styles.card,
        ...(hovered ? styles.cardHover : {}),
        ...(highlighted ? { outline: "3px solid #028891", outlineOffset: 2 } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Accent bar ── */}
      <div style={styles.accentBar} />

      <div style={styles.cardInner}>
      {/* ── TOP ROW: speaker + time ── */}
      <div style={styles.topRow}>
        <div style={styles.speaker}>
          🎤 {session.speakerName || "Host"}
        </div>
        <div style={isMobile ? styles.timeMobile : styles.time}>
          ⏰ {extractLocalTime(session.startTime)}
        </div>
      </div>

      {/* ── TOPIC ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
        {session.readingPassage ? (
          <div
            onClick={() => navigate(`/study/reading/${session.readingPassage.id}`, { state: { from: "session" } })}
            onMouseEnter={() => setTopicHovered(true)}
            onMouseLeave={() => setTopicHovered(false)}
            style={{
              ...styles.readingChip,
              fontSize: isMobile ? 17 : 20,
              fontWeight: 800,
              padding: "6px 16px",
              marginBottom: 0,
              background: topicHovered ? "#028891" : "#f0fafa",
              color: topicHovered ? "#fff" : "#00796b",
              borderColor: topicHovered ? "#028891" : "#b2dfdb",
            }}
          >
            <FaBookOpen size={isMobile ? 14 : 16} />
            {session.displayTitle || session.readingPassage.title}
          </div>
        ) : (
          <div style={{ ...(isMobile ? styles.topicMobile : styles.topic), marginBottom: 0 }}>
            {session.displayTitle || session.topic}
          </div>
        )}
        <span style={isOneOnOne ? styles.planBadgeOneOnOne : styles.planBadge}>
          {isOneOnOne ? "🔒 " : "👥 "}{getPlanTypeLabel(session.planType)}
        </span>
      </div>

      {/* ── BADGES ROW ── */}
      <div style={styles.badgesRow}>
        {session.remainingSlots !== undefined &&
          session.remainingSlots <= 3 &&
          session.remainingSlots > 0 &&
          !isExpired &&
          !isOneOnOne && (
            <span style={styles.slotsBadge}>
              ⚠️ {session.remainingSlots} slot{session.remainingSlots === 1 ? "" : "s"} left
            </span>
          )}
      </div>

      {/* ── STATUS BADGE ── */}
      {isCancelled ? (
        <div style={styles.cancelledBadge}>❌ Session Cancelled by Host</div>
      ) : isBooked ? (
        <div style={styles.bookedBadge}>✅ Session Booked. See you in the meeting!</div>
      ) : null}

      {/* ── UPGRADE REASON ── */}
      {session.buttonAction === sessionActions.UPGRADE && session.upgradeReason && (
        <div style={styles.upgradeReason}>ℹ️ {session.upgradeReason}</div>
      )}

      {/* ── DIVIDER ── */}
      <div style={styles.divider} />
      </div>{/* end cardInner */}

      {/* ── ACTION ROW ── */}
      <div style={isMobile ? styles.actionRowMobile : styles.actionRow}>
        <button
          onClick={handleShare}
          style={{ ...styles.shareBtn, ...(linkCopied ? styles.shareBtnCopied : {}) }}
        >
          {linkCopied ? <FaCheck size={11} /> : <FaShareAlt size={11} />}
          {linkCopied ? "Copied!" : "Share"}
        </button>

        <button
          style={{
            ...getButtonStyle(),
            ...(isMobile ? { width: "100%" } : { minWidth: 180 }),
          }}
          onClick={handleButtonClick}
          disabled={loadingAction || isCancelled || isExpired || isPreparing || isBookingClosed || isSessionFull}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", whiteSpace: "nowrap" }}>
            {loadingAction ? "Please wait..." : getButtonLabel()}
            {!loadingAction && !isCancelled && !isExpired && !isPreparing && !isBookingClosed && !isSessionFull && (
              <FaArrowRight size={11} />
            )}
          </span>
        </button>
      </div>

      {/* ── AGENDA ── */}
      {session.agendas?.length > 0 && (
        <>
          <button style={styles.agendaToggle} onClick={() => setOpenAgenda(!openAgenda)}>
            {openAgenda ? "▲ Hide discussion topics" : "▼ View discussion topics"}
          </button>

          {openAgenda && (
            <div style={styles.agendaWrap}>
              {session.agendas.map((agenda) => (
                <div key={agenda.id} style={styles.agendaCard}>
                  <div style={styles.agendaTitle}>📌 {agenda.title}</div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                  }}>
                    {agenda.sections?.map((section) => (
                      <div key={section.id}>
                        <div
                          style={{
                            ...styles.sectionTitle,
                            background: copiedSectionId === section.id ? "#f0fdf4" : "transparent",
                          }}
                          onClick={() => handleCopySection(section)}
                        >
                          <span style={{ color: copiedSectionId === section.id ? "#16a34a" : "#94a3b8" }}>
                            {copiedSectionId === section.id ? <FaCheck size={12} /> : <FaRegCopy size={12} />}
                          </span>
                          {section.section}
                        </div>
                        {section.questions?.map((q) => (
                          <div key={q.id} style={styles.question}>• {q.questionText}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ALERT ── */}
      {showAlert && (
        <div style={styles.customAlertOverlay}>
          <div style={styles.customAlertBox}>
            <div style={styles.customAlertText}>{alertMessage}</div>
            <button style={styles.customAlertButton} onClick={() => setShowAlert(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionCard;
